import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, ChevronsUpDown, Download, Loader2, Pencil, Plus, Star, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice, resolveImage, formatApiErrorDetail } from "@/lib/api";

const CATEGORY_LABELS = { miels: "Miels", derives: "Produits de la ruche", coffrets: "Coffrets" };
const catLabel = (p) => CATEGORY_LABELS[p.category] || p.category || "";

export default function AdminProducts() {
  const [products, setProducts] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [catFilter, setCatFilter] = useState("all");
  const [importErrors, setImportErrors] = useState([]);
  const fileInputRef = useRef(null);

  const load = () => api.get("/admin/products").then(({ data }) => setProducts(data)).catch(() => setProducts([]));
  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (p) => {
    if (!window.confirm(`Supprimer « ${p.name} » ? Cette action est définitive.`)) return;
    try {
      await api.delete(`/admin/products/${p.product_id}`);
      toast.success("Produit supprimé");
      load();
    } catch {
      toast.error("Suppression impossible, réessayez.");
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/admin/products/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `produits_petales_de_miel_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export Excel téléchargé");
    } catch {
      toast.error("Export impossible, réessayez.");
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permet de ré-importer le même fichier
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.message || "Import terminé");
      setImportErrors(data.errors || []);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import impossible");
    } finally {
      setImporting(false);
    }
  };

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const sortValue = (p, key) => {
    if (key === "name") return (p.name || "").toLowerCase();
    if (key === "weight") return (p.weight || "").toLowerCase();
    if (key === "category") return catLabel(p).toLowerCase();
    if (key === "price") return p.price_cents || 0;
    if (key === "stock") return p.stock || 0;
    return "";
  };

  const sortedProducts = products
    ? [...products].sort((a, b) => {
        const av = sortValue(a, sort.key);
        const bv = sortValue(b, sort.key);
        let cmp;
        if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
        else cmp = String(av).localeCompare(String(bv), "fr");
        return sort.dir === "asc" ? cmp : -cmp;
      })
    : products;

  const categoryOptions = products
    ? [...new Set(products.map((p) => p.category || ""))].filter(Boolean).sort((a, b) => catLabel({ category: a }).localeCompare(catLabel({ category: b }), "fr"))
    : [];

  const visibleProducts = sortedProducts
    ? catFilter === "all"
      ? sortedProducts
      : sortedProducts.filter((p) => (p.category || "") === catFilter)
    : sortedProducts;

  const SortIcon = ({ column }) => {
    if (sort.key !== column) return <ChevronsUpDown size={13} className="opacity-40" />;
    return sort.dir === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />;
  };

  const SortTh = ({ column, label, className = "" }) => (
    <th className={`px-5 py-4 ${className}`}>
      <button
        type="button"
        data-testid={`sort-${column}`}
        onClick={() => toggleSort(column)}
        className={`flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] transition-colors hover:text-amber-deep ${sort.key === column ? "text-amber-deep" : "text-stone-mute"}`}
      >
        {label} <SortIcon column={column} />
      </button>
    </th>
  );

  return (
    <div data-testid="admin-products" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-serif text-3xl font-semibold text-bark">Produits</h1>
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="export-products-button"
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 rounded-full border border-amber-800/25 bg-white px-4 py-2.5 text-sm font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep disabled:opacity-60"
          >
            {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Exporter (Excel)
          </button>
          <button
            data-testid="import-products-button"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="flex items-center gap-2 rounded-full border border-amber-800/25 bg-white px-4 py-2.5 text-sm font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep disabled:opacity-60"
          >
            {importing ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Importer (Excel)
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleImport}
            data-testid="import-products-input"
            className="hidden"
          />
          <Link
            to="/admin/produits/nouveau"
            data-testid="new-product-button"
            className="flex items-center gap-2 rounded-full bg-amber-deep px-5 py-2.5 text-sm font-medium text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95"
          >
            <Plus size={16} /> Nouveau produit
          </Link>
        </div>
      </div>

      <p className="rounded-xl bg-amber-50/70 px-4 py-3 text-xs text-bark-soft">
        Astuce : exportez d’abord pour obtenir le modèle de colonnes. Conservez la colonne « ID produit » pour mettre à jour un produit existant ; laissez-la vide pour en créer un nouveau. La « Catégorie (id) » est facultative — exportez les catégories pour connaître les identifiants disponibles.
      </p>

      {importErrors.length > 0 && (
        <div data-testid="import-errors" className="rounded-xl border border-terra/30 bg-terra/5 p-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold text-terra">{importErrors.length} ligne(s) non importée(s)</p>
            <button data-testid="import-errors-dismiss" onClick={() => setImportErrors([])} className="text-xs text-bark-soft underline hover:text-bark">Masquer</button>
          </div>
          <ul className="max-h-48 space-y-1 overflow-y-auto text-xs text-bark">
            {importErrors.map((err, i) => (
              <li key={i}>• {err}</li>
            ))}
          </ul>
        </div>
      )}

      {!products ? (
        <p data-testid="products-loading" className="py-16 text-center font-serif text-xl text-bark-soft">Chargement…</p>
      ) : products.length === 0 ? (
        <p data-testid="products-empty" className="py-16 text-center font-serif text-xl text-bark-soft">Aucun produit. Ajoutez votre première douceur !</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-xs font-medium uppercase tracking-wider text-stone-mute">Filtrer par catégorie</label>
            <select
              data-testid="product-category-filter"
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value)}
              className="rounded-xl border border-amber-800/20 bg-white px-3 py-2 text-sm text-bark outline-none focus:border-amber-deep"
            >
              <option value="all">Toutes les catégories ({products.length})</option>
              {categoryOptions.map((c) => (
                <option key={c} value={c}>
                  {catLabel({ category: c })} ({products.filter((p) => (p.category || "") === c).length})
                </option>
              ))}
            </select>
            {catFilter !== "all" && (
              <button data-testid="product-category-filter-clear" onClick={() => setCatFilter("all")} className="text-xs text-bark-soft underline hover:text-amber-deep">
                Réinitialiser
              </button>
            )}
            <span className="text-sm text-bark-soft">{visibleProducts.length} produit(s)</span>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-amber-800/15 bg-white shadow-sm">
          <table className="w-full min-w-[800px] text-left text-sm">
            <thead>
              <tr className="border-b border-amber-900/10 text-xs uppercase tracking-[0.15em] text-stone-mute">
                <SortTh column="name" label="Produit" />
                <SortTh column="weight" label="Poids" />
                <SortTh column="category" label="Catégorie" />
                <SortTh column="price" label="Prix" />
                <SortTh column="stock" label="Stock" />
                <th className="px-5 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amber-900/10">
              {visibleProducts.map((p) => (
                <tr key={p.product_id} data-testid={`admin-product-row-${p.product_id}`} className="transition-colors hover:bg-cream/60">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <img src={resolveImage(p.image_url)} alt="" className="h-12 w-12 rounded-lg border border-amber-900/10 object-cover" />
                      <div>
                        <p className="font-medium text-bark">{p.name}</p>
                        {p.featured && (
                          <span className="mt-0.5 inline-flex items-center gap-1 text-xs text-amber-deep">
                            <Star size={11} className="fill-honey text-honey" /> À la une
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td data-testid={`weight-${p.product_id}`} className="px-5 py-3 text-bark-soft">{p.weight || "—"}</td>
                  <td className="px-5 py-3 text-bark-soft">{CATEGORY_LABELS[p.category] || p.category}</td>
                  <td className="px-5 py-3 font-semibold text-bark">{formatPrice(p.price_cents)}</td>
                  <td className="px-5 py-3">
                    <span
                      data-testid={`stock-${p.product_id}`}
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        p.stock === 0 ? "bg-terra/15 text-terra" : p.stock <= 5 ? "bg-amber-100 text-amber-800" : "bg-sage-light text-sage"
                      }`}
                    >
                      {p.stock === 0 ? "Épuisé" : p.stock}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-2">
                      <Link
                        to={`/admin/produits/${p.product_id}`}
                        data-testid={`edit-product-${p.product_id}`}
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-amber-800/20 text-bark-soft transition-colors hover:border-amber-deep hover:text-amber-deep"
                        aria-label={`Modifier ${p.name}`}
                      >
                        <Pencil size={15} />
                      </Link>
                      <button
                        data-testid={`delete-product-${p.product_id}`}
                        onClick={() => handleDelete(p)}
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-terra/30 text-terra transition-colors hover:bg-terra/10"
                        aria-label={`Supprimer ${p.name}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}
    </div>
  );
}
